import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '@/lib/api-client';
import { breweryRequest } from '@/pages/breweries/api';
import endpoints from '@/api/endpoints';
import { IUserProfileData } from '@/features/sliceUserProfile/UserProfileInitialState';

// Types
export interface PublicProfileResponse {
  headshot?: string | null;
  linkedIn?: string;
  publicBio?: string;
  uguid: string;
}

export interface PublicProfileRequest {
  publicBio: string | null;
  linkedIn: string | null;
  headshot: string | null;
  uguid: string;
}

// Query keys
export const userProfileKeys = {
  all: ['userProfile'] as const,
  public: () => [...userProfileKeys.all, 'public'] as const,
  personal: () => [...userProfileKeys.all, 'personal'] as const,
  details: () => [...userProfileKeys.all, 'details'] as const,
  notAllowedEmailParts: () => [...userProfileKeys.all, 'notAllowedEmailParts'] as const,
};

const profileGet = <T,>(url: string): Promise<T> => /^\/breweries(?:\/|$)/i.test(window.location.pathname) ? breweryRequest<T>({ url }) : api.get<T>(url);

// Public Profile Hooks
export const useGetPublicProfile = (enabled: boolean = true) => {
  return useQuery({
    queryKey: userProfileKeys.public(),
    queryFn: async () => {
      return profileGet<PublicProfileResponse>(endpoints.userPublicProfile);
    },
    enabled,
  });
};

export const useUpdatePublicProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: PublicProfileRequest) => {
      return api.put<PublicProfileResponse>(endpoints.userPublicProfile, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userProfileKeys.public() });
      toast.success('Public profile updated successfully');
    },
  });
};

export const useUploadHeadshot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: FormData) => {
      return api.post<string>(endpoints.userHeadShot, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userProfileKeys.public() });
      toast.success('Headshot uploaded successfully');
    },
  });
};

// Personal Info Hooks
export const useGetPersonalInfo = (enabled: boolean = true) => {
  return useQuery({
    queryKey: userProfileKeys.personal(),
    queryFn: async () => {
      return profileGet<IUserProfileData>(endpoints.userProfile);
    },
    enabled,
  });
};

const emailConflictMessage =
  'This email address cannot be used. Please use a different one.';

export const useUpdatePersonalInfo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: IUserProfileData) => {
      return api.put<IUserProfileData>(endpoints.userProfile, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userProfileKeys.personal() });
      toast.success('Personal information updated successfully');
    },
    onError: (error: any) => {
      if (error?.response?.status === 409) {
        toast.error(emailConflictMessage);
      }
    },
  });
};

// Password Change Hook
export const useChangePassword = () => {
  return useMutation({
    mutationFn: async (data: { Password: string }) => {
      return api.put(endpoints.userChangePassword, data);
    },
    onSuccess: () => {
      toast.success('Password changed successfully');
    },
  });
};

// User Details
export interface IUserDetailsResponse {
  headShot?: string;
  userName?: string;
  email?: string;
  subscriptionTypes?: string[];
}

export const useGetUserDetails = (enabled: boolean = true) => {
  return useQuery({
    queryKey: userProfileKeys.details(),
    queryFn: async () => {
      return profileGet<IUserDetailsResponse>(endpoints.userDetails);
    },
    enabled,
  });
};

// Email Hooks
export const useCheckEmail = () => {
  return useMutation({
    mutationFn: async (data: { email: string; code?: string }) => {
      return api.post<boolean>(endpoints.checkEmail, data);
    },
    onError: (error: any) => {
      if (error?.response?.status === 409) {
        toast.error(emailConflictMessage);
      }
    },
  });
};

export const useConfirmEmail = () => {
  return useMutation({
    mutationFn: async (data: { email: string; code?: string }) => {
      return api.post<boolean>(endpoints.confirmEmail, data);
    },
  });
};

export const useGetNotAllowedEmailParts = () => {
  return useQuery({
    queryKey: userProfileKeys.notAllowedEmailParts(),
    queryFn: async () => {
      return api.get<string[]>(endpoints.getNotAllowedEmailParts);
    },
  });
};
